import React, { useState, useEffect } from "react";
import {
  Rocket,
  Play,
  Disc3,
  Grid3x3,
  Music4,
  AudioWaveform,
  SlidersHorizontal,
  Sparkles,
  ListMusic,
  Save,
  Keyboard,
  HelpCircle,
  Wand2,
  Scissors,
  Gauge,
  FileMusic,
  NotebookPen,
  Layers,
} from "lucide-react";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";

const SECTIONS = [
  {
    id: "transport",
    icon: Play,
    color: "#00ff66",
    title: "Transport & Tempo",
    body: "The top bar controls playback for the whole studio. Green button = Play/Stop, next button = full Stop, and the metronome (timer icon) clicks along to help you stay in time. Set the song speed with the BPM field (beats per minute). Undo/Redo reverse any change.",
  },
  {
    id: "decks",
    icon: Disc3,
    color: "#00e5ff",
    title: "Decks (Turntables)",
    body: "In the Decks tab, click LOAD on Deck A or B to drop in a song, press the deck's play button, and the record spins. Click and drag the spinning platter to scratch. The Pitch slider speeds up / slows down the track. Use the crossfader to blend the two decks, and Headphone Cue to pre-listen to a deck before bringing it in.",
  },
  {
    id: "drums",
    icon: Grid3x3,
    color: "#ffb800",
    title: "Drum Machine & Kits",
    body: "In Beats & Keys, tap a Preset (House, Boom Bap, Trap, Techno, Funk) for an instant beat, or click grid squares to program your own — each row is a drum, each column is a step. RIGHT-CLICK a hi-hat/snare step to add a ROLL (×2 / ×3 / ×4) — the retriggering 'brrrt' that makes trap hats sound real. Choose the step count (16 / 32 / 64) for longer patterns, pick a real drum Kit (Acoustic, 808 Analog, 909 Analog, Distorted, Synth, Deep House), and build a song with Patterns A–D laid across bars in the Arrangement row. Swing adds groove; the per-drum slider/dot sets volume and mute.",
  },
  {
    id: "synth",
    icon: Music4,
    color: "#00ff66",
    title: "Keys & Real Instruments",
    body: "The keyboard plays a real Grand Piano by default — play with your mouse or your computer keys (A S D F… white keys, W E T Y U black). Use ◀ ▶ or Up/Down arrows to change octave. The Sound menu is loaded with REAL recorded instruments — Grand Piano, Rhodes, Electric Bass, Strings, Clean Guitar, Saxophone, and a full jazz horn section: Trumpet, Flugelhorn, Trombone, French Horn, Tuba, Bugle and a smoky Muted Trumpet — plus synth sounds and a knocking 808 Sub for trap. To record a part: press Play, hit REC, and play — your notes are captured and auto-quantized. Clear erases the part.",
  },
  {
    id: "ai",
    icon: Wand2,
    color: "#ff007f",
    title: "AI Song Starter & Beat Lab",
    body: "Stuck on a blank page? Describe what you want ('dark menacing trap beat', 'smooth jazz with a trumpet solo') and the AI Song Starter builds a full editable beat — drums, a knocking 808, and melodies played on the REAL instruments — matched to a key and tempo. Everything it makes is yours to edit. Hard rap/trap beats come out drum + 808 driven (no cheesy keyboard). The Beat Lab is a free 'idea sparker' that generates a rough audio loop you can send straight to the Sampler to chop.",
  },
  {
    id: "remix",
    icon: Sparkles,
    color: "#ffb800",
    title: "Remix Bar (swap sounds live)",
    body: "When a beat has melody parts, the Remix bar appears above the grid. Swap the drum Kit or any part's instrument (bass / chords / lead) instantly — no regenerate. Mute a part, hit Drums Only to isolate the beat, Clear Melody to keep just the drums, or tap Horn Stabs to drop a funk/soul brass-stab riff that follows your song's key automatically.",
  },
  {
    id: "songwriter",
    icon: NotebookPen,
    color: "#a855f7",
    title: "Songwriter (AI writing assistant)",
    body: "Tap Write in the top bar to open the Songwriter — a notepad with an AI co-writer built in. It knows your current beat's BPM and key, so its suggestions fit the groove. Type a topic (or a word to rhyme), then tap Hook, Verse, Rhymes, Cadence, Ideas or Continue and it hands you a starting point you can drop straight into your pad and rewrite. IMPORTANT — this is an ASSISTANT, not a ghostwriter: it sparks ideas, hooks, rhymes and flow to get you unstuck, but it will NOT write your whole song for you, and it shouldn't. The best lines are yours — use the AI to break through a blank page, then make it your own. Your lyrics are saved on your device automatically. (Free plans include a set number of writing assists per day; Pro unlocks unlimited co-writing.)",
  },
  {
    id: "sections",
    icon: Layers,
    color: "#a855f7",
    title: "Song Sections (structure your track)",
    body: "The Sections tab lays your song out like a real arrangement — Intro, Verse, Hook, Drop, Bridge, Outro and more. Add sections with the buttons up top, choose which drum pattern (A–D) each one uses and how many bars it lasts, then drag the cards (or use the ▲▼ arrows) to reorder your song. Hit Apply to Beat to arrange your drum patterns in that order. Tip: build a few different patterns in the Drum Machine first (A = verse groove, B = hook, C = drop), then use Sections to arrange them into a full song.",
  },
  {
    id: "sampler",
    icon: Scissors,
    color: "#22d3ee",
    title: "Sampler & Vocal Remover",
    body: "In the Sampler tab, drop in any song and split it into separate stems — instrumental, vocals, drums, bass and melody. Then chop any part into playable pads (Grid / Auto / Manual slicing) and perform or re-sequence it. Great for flipping a sample or pulling an a cappella.",
  },
  {
    id: "score",
    icon: FileMusic,
    color: "#00e5ff",
    title: "Score (sheet music)",
    body: "The Score tab writes your beat out as REAL sheet music on a staff. Pick a part (Lead / Chords / Bass), Transpose it for concert or Bb (trumpet, tenor sax), Eb (alto sax) or F (French horn) instruments, press Play to hear it on the real instruments, and Print / PDF clean parts to hand to your band. You can also IMPORT a MIDI or MusicXML file (a part written in MuseScore, Finale or Noteflight) — drop a beat under it and flip it — and EXPORT your notes back out as MusicXML. Turn on Edit to click notes right onto the staff. Producer and composer in one place.",
  },
  {
    id: "timeline",
    icon: AudioWaveform,
    color: "#a855f7",
    title: "Timeline & Tracks",
    body: "In the Timeline tab, each lane is a track. Click the mic to record from your microphone, or the upload icon to import an audio file — you'll see its waveform. Click Add Track for more lanes, rename a track by clicking its name, and remove one with the × button.",
  },
  {
    id: "mixer",
    icon: SlidersHorizontal,
    color: "#22d3ee",
    title: "Mixing Console",
    body: "The console at the bottom has one channel strip per source (decks, drums, synth, tracks) plus Master. Drag the fader for volume, the Pan knob for left/right, M to mute, S to solo. Click a strip to load its controls into the Effects Rack.",
  },
  {
    id: "effects",
    icon: Sparkles,
    color: "#ff007f",
    title: "Effects Rack & EQ",
    body: "The right panel shows effects for the selected channel: an 8-band graphic EQ (drag sliders up/down to boost or cut frequencies, Flat resets), plus Compressor, Drive, Filter, Delay and Reverb knobs. The Live Spectrum at the top shows your sound in real time.",
  },
  {
    id: "player",
    icon: ListMusic,
    color: "#00ff66",
    title: "Player",
    body: "The Player tab is a simple music player. Add your audio files, then Play / Next / Previous, Shuffle or Loop. The Player has its own mixer channel — while you're on the Player tab the Effects Rack targets it, so the 8-band EQ, reverb and other effects shape your music as it plays.",
  },
  {
    id: "master",
    icon: Gauge,
    color: "#ff9500",
    title: "Master (finish your song)",
    body: "In the Master tab, upload a finished song and shape its tone and loudness with one-tap presets, A/B it against the original to hear the difference, then download a hi-def mastered WAV — ready to release.",
  },
  {
    id: "save",
    icon: Save,
    color: "#00e5ff",
    title: "Saving & Exporting",
    body: "Save stores your whole session (beat, mixer, effects, instruments, melody and score) to the cloud — reopen it with Load, or share a link. Export renders your song to WAV: Mixdown makes one file, or export Individual Stems (one WAV per channel) for mixing elsewhere. (Free accounts get a set number of saves and AI starts per day; Pro unlocks unlimited saves, unlimited AI and WAV export.)",
  },
];

const SHORTCUTS = [
  ["A S D F G H J K L", "Play white piano keys"],
  ["W E T Y U O", "Play black piano keys"],
  ["Q 2 W 3 E …", "Upper octave on the keyboard"],
  ["↑ / ↓ arrows", "Shift the keyboard octave"],
  ["Right-click a step", "Add a hi-hat / snare ROLL (×2 / ×3 / ×4)"],
];

const FAQ = [
  {
    q: "Does the AI write my whole song for me?",
    a: "No — and that's on purpose. The Songwriter is an ASSISTANT (that's why we call it assist). It helps you break a blank page: hooks, verse starters, rhymes and cadence ideas that match your beat's tempo and key. But the words that connect are yours. Use it to get unstuck, then rewrite the suggestions in your own voice.",
  },
  {
    q: "How do I make a hard-hitting trap beat?",
    a: "Pick the Trap preset (or ask the AI Song Starter for a 'hard trap beat'), and it uses the 808 Analog kit with a knocking, sliding 808 Sub bass. For the classic rolling hats, right-click hi-hat steps to add ×3 / ×4 rolls. Keep the melody sparse so the drums and 808 carry it.",
  },
  {
    q: "How do I get real horns / a jazz sound?",
    a: "Open the Sound menu on the keys — you'll find real recorded Trumpet, Flugelhorn, Trombone, French Horn, Tuba, Muted Trumpet and Saxophone. Or on any beat, click Horn Stabs in the Remix bar for an instant funk/soul brass riff in your key.",
  },
  {
    q: "Can I turn my beat into sheet music for a band?",
    a: "Yes — open the Score tab. It writes your notes onto a staff. Pick a part, transpose it for Bb/Eb/F instruments (like trumpet or alto sax), and Print / PDF a clean part to hand out. Press Play to hear it on the real instruments first.",
  },
  {
    q: "The AI keeps adding a keyboard/melody I don't want.",
    a: "For rap/trap it now comes out drums + 808 only. On any beat you can hit Drums Only or Clear Melody in the Remix bar to strip the melodic parts and keep just the beat.",
  },
  {
    q: "I can't hear anything — what's wrong?",
    a: "Browsers only start audio after you interact, so click Play or any control first. Then check the Master fader isn't down and no channel is muted (red M). Also make sure your computer volume is up.",
  },
  {
    q: "How do I record my voice or an instrument?",
    a: "Go to the Timeline tab and click the microphone icon on a track. Allow microphone access when your browser asks. Click the stop button to finish — your take appears as a waveform on that track.",
  },
  {
    q: "How do I split the vocals off a song?",
    a: "Use the Sampler tab — drop in a song and it separates into instrumental, vocals, drums, bass and melody. You can then chop any stem into playable pads.",
  },
  {
    q: "Can I play with my computer keyboard?",
    a: "Yes! On the piano, the A S D F… row plays notes and Up/Down arrows change octave. No MIDI keyboard needed.",
  },
  {
    q: "Where do my exported songs go?",
    a: "Export creates WAV files that download to your browser's Downloads folder. Mixdown = the full song; Stems = one file per instrument/track. (WAV export is a Pro feature.)",
  },
  {
    q: "How do I save and come back later?",
    a: "Click Save (top right) to store the session in the cloud, and Load to reopen it. Everything — beat, mix, melody and score — comes back exactly as you left it.",
  },
  {
    q: "I'm experienced — how do I stop the welcome guide?",
    a: "Uncheck “Show this guide on startup” in the welcome window. You can always reopen it with the Help button or read this Manual anytime.",
  },
];

export default function Manual({ focus }) {
  const [openSec, setOpenSec] = useState(focus ? `sec-${focus}` : undefined);

  useEffect(() => {
    if (focus) {
      setOpenSec(`sec-${focus}`);
      const el = document.querySelector(`[data-testid="manual-section-${focus}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [focus]);

  return (
    <div className="h-full overflow-y-auto p-6 md:p-10" data-testid="manual">
      <div className="max-w-3xl mx-auto flex flex-col gap-8">
        {/* header */}
        <div>
          <h1 className="font-display text-2xl font-black tracking-widest text-neutral-100">USER MANUAL</h1>
          <p className="text-neutral-500 text-sm mt-1">
            Everything you need to make music in BeatMXR — no experience required.
          </p>
        </div>

        {/* quick start */}
        <div className="rounded-md border border-[#262626] bg-[#111] p-5">
          <div className="flex items-center gap-2 mb-3">
            <Rocket size={18} className="text-[#00e5ff]" />
            <h2 className="font-display text-base uppercase tracking-widest text-neutral-200">Quick Start</h2>
          </div>
          <ol className="flex flex-col gap-2 text-sm text-neutral-300 list-decimal list-inside">
            <li>Go to <span className="text-[#ffb800] font-semibold">Beats &amp; Keys</span> and tap a preset like <span className="text-[#ffb800]">Trap</span> — or click <span className="text-[#ff007f] font-semibold">AI Start</span> and describe the beat you want.</li>
            <li>Press the big green <span className="text-[#00ff66] font-semibold">Play</span> button at the top — you're making music.</li>
            <li>Play the <span className="text-[#00e5ff] font-semibold">real instruments</span> along with it, remix the sounds live, then <span className="text-[#00e5ff] font-semibold">Save</span>, <span className="text-[#00e5ff] font-semibold">Export</span> a WAV, or open <span className="text-[#00e5ff] font-semibold">Score</span> to print it for your band.</li>
          </ol>
        </div>

        {/* sections */}
        <div>
          <h2 className="font-display text-base uppercase tracking-widest text-neutral-300 mb-3">
            How everything works
          </h2>
          <Accordion type="single" collapsible value={openSec} onValueChange={setOpenSec} className="flex flex-col gap-2">
            {SECTIONS.map((s) => {
              const Icon = s.icon;
              return (
                <AccordionItem
                  key={s.id}
                  value={`sec-${s.id}`}
                  className="border border-[#262626] rounded-sm bg-[#111] px-4"
                  data-testid={`manual-section-${s.id}`}
                >
                  <AccordionTrigger className="hover:no-underline py-3">
                    <span className="flex items-center gap-3">
                      <Icon size={16} style={{ color: s.color }} />
                      <span className="font-semibold text-sm text-neutral-200">{s.title}</span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-neutral-400 leading-relaxed pb-4">
                    {s.body}
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>

        {/* shortcuts */}
        <div className="rounded-md border border-[#262626] bg-[#111] p-5">
          <div className="flex items-center gap-2 mb-3">
            <Keyboard size={18} className="text-[#a855f7]" />
            <h2 className="font-display text-base uppercase tracking-widest text-neutral-200">Keyboard Shortcuts</h2>
          </div>
          <div className="flex flex-col gap-2">
            {SHORTCUTS.map(([k, v]) => (
              <div key={k} className="flex items-center justify-between text-sm border-b border-[#1a1a1a] pb-2">
                <span className="font-mono-data text-[#00e5ff] text-xs">{k}</span>
                <span className="text-neutral-400">{v}</span>
              </div>
            ))}
          </div>
        </div>

        {/* FAQ */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <HelpCircle size={18} className="text-[#00ff66]" />
            <h2 className="font-display text-base uppercase tracking-widest text-neutral-300">FAQ</h2>
          </div>
          <Accordion type="single" collapsible className="flex flex-col gap-2">
            {FAQ.map((f, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                className="border border-[#262626] rounded-sm bg-[#111] px-4"
                data-testid={`faq-${i}`}
              >
                <AccordionTrigger className="hover:no-underline py-3 text-left">
                  <span className="font-semibold text-sm text-neutral-200">{f.q}</span>
                </AccordionTrigger>
                <AccordionContent className="text-sm text-neutral-400 leading-relaxed pb-4">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        <p className="text-center text-neutral-600 text-xs pb-4">
          Still stuck? Reopen the guided tour with the <span className="text-[#00e5ff]">Help</span> button up top.
        </p>
      </div>
    </div>
  );
}
